import React, { useState } from "react";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "../../../styles/components/CircleGraph.scss";
import hexToRgb from "../../../common/function/hexToRgb";

interface CircleGraphProps {
  objectsArray: Array<Record<string, any>>;
  property: string;
}

const predefinedCategories = {
  age: [
    { label: "18–30", min: 18, max: 30 },
    { label: "31–44", min: 31, max: 44 },
    { label: "45–54", min: 45, max: 54 },
    { label: "55–64", min: 55, max: 64 },
    { label: "65–74", min: 65, max: 74 },
    { label: "75–84", min: 75, max: 84 },
    { label: ">85", min: 85, max: Infinity },
  ],
  BMI: [
    { label: "Underweight (< 18.5)", min: 0, max: 18.499 },
    { label: "Normal (18.5–24.9)", min: 18.5, max: 24.9 },
    { label: "Overweight (25–29.9)", min: 25, max: 29.9 },
    { label: "Obese (30–39.9)", min: 30, max: 39.9 },
    { label: "Morbidly obese (> 40)", min: 40, max: Infinity },
  ],
  gender: [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
    { label: "Other", value: "Other" },
  ],
  smokingStatus: [
    { label: "Over 5yrs", value: "Cessation Over 5yrs" },
    { label: "Less than 5yrs", value: "Cessation Less than 5yrs" },
    { label: "Over a pack/day", value: "Yes (Over a pack/day)" },
    { label: "Less than a pack/day", value: "Yes (Less than a pack/day)" },
    { label: "No", value: "No" },
    // "Unknown" is intentionally excluded to match Figma (only 5 items shown)
  ],
  race: [
    {
      label: "Native",
      value: "American Indian / Alaska Native",
    },
    { label: "Asian", value: "Asian" },
    {
      label: "Pacific",
      value: "Native Hawaiian / Pacific Islander",
    },
    { label: "Hispanic", value: "Hispanic / Latino" },
    { label: "White", value: "White" },
    { label: "Black", value: "Black / African American" },
    { label: "Unknown", value: "Other" },
  ],
  isSymptomatic: [
    { label: "Yes", value: "Yes" },
    { label: "No", value: "No" },
  ],
};
const CircleGraph: React.FC<CircleGraphProps> = ({
  objectsArray,
  property,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const categories =
    predefinedCategories[property as keyof typeof predefinedCategories] || [];


  const mappedObjectsArray = objectsArray.map((obj) => {
    if (property === "isSymptomatic" && typeof obj[property] === "boolean") {
      return {
        ...obj,
        [property]: obj[property] ? "Yes" : "No",
      };
    }
    return obj;
  });

  const valueCounts = categories.map((category: any) => {
    if (category.min !== undefined && category.max !== undefined) {
      return mappedObjectsArray.reduce((count, obj) => {
        const value = obj[property];
        if (value != null && value >= category.min && value <= category.max) {
          return count + 1;
        }
        return count;
      }, 0);
    } else {
      return mappedObjectsArray.reduce((count, obj) => {
        return obj[property] === category.value ? count + 1 : count;
      }, 0);
    }
  });

  const totalObjects = mappedObjectsArray.length;

  const labels = categories.map((category) => category.label);
  const percentages = valueCounts.map((count) =>
    totalObjects > 0 ? ((count / totalObjects) * 100) : 0
  );

  const colors = [
    "#00C7BE",
    "#2F75DA",
    "#EE4AE7",
    "#6E33ED",
    "#AF52DE",
    "#F12889",
    "#FF9F09",
  ];

  const dataToGraph = {
    labels: labels,
    datasets: [
      {
        data: valueCounts,
        backgroundColor: colors.map((color, index) =>
          activeIndex === null || activeIndex === index
            ? color
            : `rgba(${hexToRgb(color)}, 0.2)`
        ),
        hoverBackgroundColor: colors,
        borderRadius: 8,
        borderWidth: 5,
        borderColor: "#fff",
        hoverBorderColor: "#fff",
      },
    ],
  };

  // Unified Chart.js options for ALL donut charts - identical thickness, size, behavior
  const options = {
    responsive: false, // Disable responsive to prevent auto-scaling
    maintainAspectRatio: false, // We control size explicitly
    cutout: "68%", // Fixed cutout percentage for consistent thickness across all donuts
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    onHover: (event: any, chartElement: any[]) => {
      if (chartElement.length) {
        setActiveIndex(chartElement[0].index);
      } else {
        setActiveIndex(null);
      }
    },
    onLeave: () => {
      setActiveIndex(null);
    },
  };
  function roundTo100(rawPercentages: number[]) {
    try {
    const floors = rawPercentages.map((p) => Math.floor(p));
    let diff = 100 - floors.reduce((a, b) => a + b, 0);
  
    const frac = rawPercentages
      .map((p, i) => ({ i, f: p - Math.floor(p) }))
      .sort((a, b) => b.f - a.f);
  
    const result = [...floors];
  
    // distribute remaining points to biggest fractions
    for (let k = 0; k < diff; k++) {
      result[frac[k].i] += 1;
      }
    
      return result;
    } catch (error) {
      console.error(error);
      return rawPercentages;
    }
  }
  const rawPercentages = valueCounts.map((count) =>
    totalObjects > 0 ? (count / totalObjects) * 100 : 0
  );
  
  const intPercentages = roundTo100(rawPercentages);
  
  const isRaceEthnicity = property === "race";
  
  // Filter out zero-value categories
  // For race: hide any category with 0%
  const filteredIndices = labels.map((label, index) => {
    if (valueCounts[index] === 0) {
      // Hide zero-value categories
      if (isRaceEthnicity) {
        return null;
      }
    }
    return index;
  }).filter((idx): idx is number => idx !== null);
  
  // For Race/Ethnicity, use explicit order from Figma: Native, Asian, Pacific, Hispanic, White, Black, Unknown
  const raceOrder = ["Native", "Asian", "Pacific", "Hispanic", "White", "Black", "Unknown"];
  let displayIndices = filteredIndices;
  
  if (isRaceEthnicity) {
    displayIndices = raceOrder
      .map(orderLabel => {
        const idx = labels.findIndex(l => l === orderLabel);
        return idx >= 0 && filteredIndices.includes(idx) ? idx : null;
      })
      .filter((idx): idx is number => idx !== null);
  }
  
  return (
    <div className="circle-graph-container">
      {objectsArray.length ? (
        <ul className={`legend-list ${isRaceEthnicity ? "race-ethnicity-grid" : ""}`}>
          {displayIndices.map((index) => {
            const label = labels[index];
            const percentage = intPercentages[index];
            
            return (
              <li
                key={`${label}-${index}`}
                style={{
                  opacity:
                    activeIndex === null || activeIndex === index ? 1 : 0.5,
                  transition: "opacity 0.3s ease",
                }}
              >
                <div className="color-marker-wrapper">
                  <div
                    className="color-marker"
                    style={{
                      backgroundColor: colors[index],
                      opacity:
                        activeIndex === null || activeIndex === index ? 1 : 0.2,
                      transition: "opacity 0.3s ease",
                    }}
                  ></div>
                </div>
                <div className="label-value-group">
                  <div className="label-text">{label}</div>
                  <div className="percentage-text">{Math.round(percentage)}%</div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
      <div className="graph" onMouseLeave={() => setActiveIndex(null)}>
        <Doughnut
          data={dataToGraph}
          options={options}
          width={160}
          height={160}
          className="doughnut-graph"
        />
      </div>
    </div>
  );
};

export default CircleGraph;
